import * as React from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
    BasicForm,
    ICardProps,
    FormItem,
    TextFieldItem,
    EmailFieldItem,
    SelectFormItem,
    MultipleSelectFormItem,
    IconTextButton,
    Skeleton,
    Spinner,
    DangerAlert,
    showInformationDialog,
} from 'ui-lib';

import { getErrorMessage, isNullOrEmpty } from 'common-utils';

import { useAccountStore, useAuthStore, useRoleStore, useRoutesStore, useUserStore } from '../../../../store';
import { IEditUserProps, ISaveUserProps, IUser } from '../../../../dto/access-management/user-dto';
import { OrganisationSelect } from '../../components/organisation-select';
import { confirmCancel, deleteUser } from '../utils';
import { USER_ROUTE_CODES } from './';

import manageUserCss from './user.css';
import { useAlertMessages } from 'pages/private/components/alert/use-messages';
import { MessagesAlert } from 'pages/private/components/alert/messages-alert';
import { IRole } from 'dto/access-management/user-role-dto';
import { IAccount } from 'dto/access-management/account-dto';

const INVALID_EMAIL_ERROR = 'This email address is already being used';

function validateEmail(email: string) {
    return email.match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
}
const roleToSelectDataSourceFn = (role: IRole): { label: string; value: number; } => ({ value: role.id, label: role.roleType });
interface IAddOrEditUserComponentProps {
    isEdit?: boolean;
    cardPros?: ICardProps;
    userInfo: IEditUserProps;
}

const SaveUser = (props: IAddOrEditUserComponentProps): JSX.Element => {
    const { currentUser, isDPUAdmin } = useAuthStore();
    const accountStore = useAccountStore();
    const userStore = useUserStore();
    const roleStore = useRoleStore();
    const navigate = useNavigate();

    const routeStore = useRoutesStore();
    const rootRoute = routeStore.getRoute(USER_ROUTE_CODES.ROOT);

    const [userInView, setUserInView] = React.useState(props.userInfo);
    const [disableComponent, setDisableComponent] = React.useState(false);
    const [isProcessing, setIsProcessing] = React.useState(false);

    const [selectedOrganisation, setSelectedOrganisation] = React.useState<IAccount>(
        () => {
            const accountId = !props.userInfo.organisationId ? currentUser.accountId : props.userInfo.organisationId;
            return accountStore.getAccountById(accountId);
        }
    );

    const [selectedRole, setSelectedRole] = React.useState(props.userInfo.userRoleId);
    const [managedRoles, setManagedRoles] = React.useState<IRole[]>([]);

    React.useEffect(() => {
        if (isNullOrEmpty(managedRoles)) {
            return;
        }
        setSelectedRole((current) => managedRoles.find((item) => item.id === current)?.id as number);
    }, [managedRoles]);

    React.useEffect(() => {
        if (!selectedOrganisation) {
            setManagedRoles([]);
            return;
        }
        (async () => {
            clearMessages();
            try {
                const roles = await roleStore.getAvailableRoles(selectedOrganisation.id, selectedOrganisation.accountType);
                setManagedRoles(roles);
            } catch (err) {
                addErrorMessages(getErrorMessage(err));
            }
        })();
    }, [selectedOrganisation]);

    interface IApplicationState {
        allApplications: { id: number; value: string; label: string }[];
        selectedApplications: { id: number; value: string; label: string }[];
    }

    type ApplicationAction =
        | { type: 'SET_ALL_APPLICATIONS'; payload: any[] }
        | { type: 'SET_SELECTED_APPLICATIONS'; payload: any[] };

    const [{ allApplications, selectedApplications }, applicationDispatch] = React.useReducer(
        (state: IApplicationState, action: ApplicationAction) => {
            switch (action.type) {
                case 'SET_ALL_APPLICATIONS':
                    return { ...state, allApplications: action.payload };
                case 'SET_SELECTED_APPLICATIONS':
                    return { ...state, selectedApplications: action.payload };
                default:
                    return state;
            }
        },
        {
            allApplications: props.userInfo.selectedApplications,
            selectedApplications: props.userInfo.selectedApplications,
        },
    );

    const { errorMessages, addErrorMessages, clearMessages } = useAlertMessages();

    React.useEffect(() => {
        onSelectedOrganisationChange();
    }, [selectedOrganisation]);

    const onSelectedOrganisationChange = () => {
        try {
            setIsProcessing(true);
            if (!selectedOrganisation) {
                applicationDispatch({ type: 'SET_ALL_APPLICATIONS', payload: [] });
                applicationDispatch({ type: 'SET_SELECTED_APPLICATIONS', payload: [] });
                return;
            }
            const allApplicationsForOrg = selectedOrganisation.applications.map((app) => ({
                id: app.id,
                value: app.accessId,
                label: app.label,
            }));
            applicationDispatch({ type: 'SET_ALL_APPLICATIONS', payload: allApplicationsForOrg });
            const appValues = selectedApplications.map((item) => item.value);
            applicationDispatch({
                type: 'SET_SELECTED_APPLICATIONS',
                payload: allApplicationsForOrg.filter((item) => appValues.includes(item.value)),
            });
        } catch (error) {
            addErrorMessages(getErrorMessage(error));
        } finally {
            setIsProcessing(false);
        }
    };

    const onSave = async (values: any) => {
        try {
            setIsProcessing(true);
            clearMessages();
            const saveUserInfo: ISaveUserProps = {
                id: userInView.id,
                name: values.name,
                email: values.email,
                username: values.email,
                organisationId: selectedOrganisation?.id,
                userRoleId: values.userRole,
                oldUserRoleId: values.userRole === userInView.oldUserRoleId ? undefined : userInView.oldUserRoleId,
                selectedApplicationAccessIds: selectedApplications.map((item) => item.value),
            };

            await userStore.saveUser(userInView.id, saveUserInfo);
            navigateToList();
        } catch (error) {
            addErrorMessages(getErrorMessage(error));
        } finally {
            setIsProcessing(false);
        }
    };

    const onEmailBlur = async (event: any): Promise<void> => {
        clearMessages();
        const email = event.target.value;
        if (props.isEdit || email.toLowerCase() === userInView.email.toLowerCase()) return;

        try {
            setIsProcessing(true);
            if (!validateEmail(email) || userStore.isUserExisting(email)) return;

            setUserInView({ ...userInView, email });
            const existingUser = await userStore.getUserByEmail(email);

            if (existingUser) {
                if (!isDPUAdmin) {
                    setDisableComponent(true);
                    showInformationDialog({
                        title: 'Add new user',
                        content: INVALID_EMAIL_ERROR,
                        modalType: 'error',
                    });
                    return;
                }
                setUserInView(createIEditUserProps({ ...existingUser, roles: undefined }));
                setSelectedOrganisation(accountStore.getAccountById(existingUser.account?.id));
            } else if (disableComponent) {
                setUserInView({ ...props.userInfo, email });
            }
            setDisableComponent(!!existingUser);
        } catch (error) {
            addErrorMessages(getErrorMessage(error));
        } finally {
            setIsProcessing(false);
        }
    };

    const navigateToList = (): void => {
        navigate(rootRoute.path);
    };

    const onCancel = (): void => {
        confirmCancel(navigateToList);
    };

    const onNameChange = (event: any) => (userInView.name = event.target.value);
    const onUserRoleChange = (value: any) => setSelectedRole(value);
    const onApplicationChange = (values: any[]) =>
        applicationDispatch({
            type: 'SET_SELECTED_APPLICATIONS',
            payload: allApplications.filter((item) => values.includes(item.value)),
        });

    const emailValidator = (rule, value, callback) =>
        callback(props.isEdit || !userStore.isUserExisting(value) ? undefined : INVALID_EMAIL_ERROR);

    const email = (
        <EmailFieldItem
            code='email'
            label='Email'
            labelAlign='right'
            isRequired={true}
            initialValue={userInView.email}
            disabled={props.isEdit}
            onBlur={onEmailBlur}
            validator={emailValidator}
        />
    );
    const name = (
        <TextFieldItem
            code='name'
            label='Name'
            labelAlign='right'
            isRequired={true}
            initialValue={userInView.name}
            disabled={disableComponent}
            onChange={onNameChange}
        />
    );

    const organisation = (
        <FormItem
            code='organisation'
            label='Organisation'
            labelAlign='right'
            labelCol={{ span: 5 }}
            colon={false}
            required={true}
            child={
                <OrganisationSelect
                    value={selectedOrganisation?.id}
                    expandAll={true}
                    treeDataSource={accountStore.treeDataSource}
                    onChange={(value) => {
                        setSelectedOrganisation(accountStore.getAccountById(value));
                    }}
                />
            }
        />
    );
    const userRole = (
        <SelectFormItem
            code='userRole'
            label='User Role'
            labelAlign='right'
            isRequired={true}
            selectedValue={selectedRole}
            dataSource={managedRoles.map(roleToSelectDataSourceFn)}
            onChange={onUserRoleChange}
        />
    );
    const applications = (
        <MultipleSelectFormItem
            isRequired={true}
            code='applications'
            label='Applications'
            labelAlign='right'
            selectedValue={selectedApplications}
            dataSource={allApplications}
            onChange={onApplicationChange}
        />
    );
    const cancelButton = <IconTextButton key='cancel' label='Cancel' onClick={onCancel} />;
    const submitButton = (
        <IconTextButton key='save' label='Save' type='primary' htmlType='submit' loading={isProcessing} />
    );
    const formFunctions = <div className={manageUserCss.control}>{[cancelButton, submitButton]}</div>;

    const SaveForm = observer(() => {
        return (
            <BasicForm
                key='addOrEdit'
                className={manageUserCss.save_form}
                cardPros={props.cardPros}
                items={[email, name, organisation, userRole, applications, formFunctions]}
                onSubmit={onSave}
            />
        );
    });

    return (
        <React.Fragment>
            <MessagesAlert className={manageUserCss.alert} errorMessages={errorMessages} />
            <Spinner className={manageUserCss.spinner} spinning={isProcessing}>
                <SaveForm />
            </Spinner>
        </React.Fragment>
    );
};

export function AddUserComponent(): JSX.Element {
    const { currentUser } = useAuthStore();
    const searchParams = new URLSearchParams(location.search);
    const orgId = parseInt(searchParams.get('org'));
    const activeApplication = currentUser.applications[0];
    return (
        <SaveUser
            cardPros={{ bordered: false }}
            userInfo={{
                name: '',
                username: '',
                email: '',
                organisationId: Number.isNaN(orgId) ? undefined : orgId,
                userRoleId: undefined,
                selectedApplications: [
                    {
                        id: activeApplication?.id,
                        value: activeApplication?.accessId,
                        label: activeApplication?.label,
                    },
                ],
            }}
        />
    );
}

function createIEditUserProps(user: IUser): IEditUserProps {
    const userRoleId = isNullOrEmpty(user.roles) ? undefined : user.roles[0].id;
    return {
        id: user.id,
        username: user.username || user.email,
        name: user.name,
        email: user.email,
        organisationId: user.account.id,
        userRoleId,
        oldUserRoleId: userRoleId,
        selectedApplications: user.applications?.map((app) => ({
            id: app.id,
            accessId: app.accessId,
            value: app.accessId,
            label: app.label,
        })),
    };
}

export function EditUserComponent(): JSX.Element {
    const userStore = useUserStore();
    const navigate = useNavigate();

    const routeStore = useRoutesStore();
    const rootRoute = routeStore.getRoute(USER_ROUTE_CODES.ROOT);

    const [editUser, setEditUser] = React.useState(undefined);
    const [loadingUser, setLoadingUser] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState(null);

    const { id } = useParams();

    const onLoad = (): void => {
        (async (): Promise<void> => {
            try {
                setLoadingUser(true);
                setErrorMessage(null);
                const editUser = await userStore.getUserById(id);
                if (!editUser || editUser == null) {
                    navigate(rootRoute.path);
                    return null;
                }
                setEditUser(createIEditUserProps(editUser));
            } catch (err) {
                setErrorMessage(getErrorMessage(err));
            } finally {
                setLoadingUser(false);
            }
        })();
    };
    React.useEffect(onLoad, []);

    const onDelete = async (id: string): Promise<void> => {
        await userStore.deleteUser(id).catch((err) => setErrorMessage(getErrorMessage(err)));
        navigate(rootRoute.path);
    };
    const deleteButton = (
        <IconTextButton
            label='Delete'
            type='link'
            htmlType='button'
            onClick={(): void => deleteUser(editUser.id, onDelete)}
        />
    );

    return errorMessage ? (
        <DangerAlert message={errorMessage} className={manageUserCss.alert} />
    ) : (
        <Skeleton loading={loadingUser}>
            {!editUser ? null : (
                <SaveUser isEdit={true} cardPros={{ title: editUser.name, extra: deleteButton }} userInfo={editUser} />
            )}
        </Skeleton>
    );
}
